import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { 
  BookOpenIcon, 
  UsersIcon, 
  CurrencyDollarIcon,
  ClockIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface CourseFormData {
  title: string
  description: string
  subject: string
  expectedPricePerHour: string
  duration: string
  courseType: 'one-to-one' | 'one-to-many' | 'group'
  maxStudents: string
  minStudents: string
  groupPricePerHour: string
  groupDiscount: string
  groupDescription: string
  allowSingleStudent: boolean
}

const subjects = [
  '数学', '物理', '化学', '生物', '英语', '语文', '历史', '地理', 
  '计算机', '编程', '音乐', '美术', '体育', '其他'
]

const CreateGroupCourse: React.FC = () => {
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    description: '',
    subject: '数学',
    expectedPricePerHour: '',
    duration: '1',
    courseType: 'one-to-many',
    maxStudents: '6',
    minStudents: '2',
    groupPricePerHour: '',
    groupDiscount: '0.8',
    groupDescription: '',
    allowSingleStudent: true
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // 验证表单
      if (!formData.title || !formData.description || !formData.expectedPricePerHour) {
        throw new Error('请填写所有必填字段')
      }

      if (parseInt(formData.maxStudents) < 2) {
        throw new Error('群组课程最大学生数至少为2人')
      }

      if (parseInt(formData.minStudents) < 1 || parseInt(formData.minStudents) > parseInt(formData.maxStudents)) {
        throw new Error('最小学生数必须在1到最大学生数之间')
      }

      const response = await fetch('/api/courses/teacher', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '创建课程失败')
      }

      const data = await response.json()
      
      // 成功提示
      alert('群组课程创建成功！等待管理员审核。')
      
      // 跳转回课程列表
      navigate('/teacher/courses')
    } catch (error) {
      console.error('创建课程失败:', error)
      setError(error instanceof Error ? error.message : '创建课程失败')
    } finally {
      setLoading(false)
    }
  }

  const calculateGroupPrice = () => {
    const basePrice = parseFloat(formData.expectedPricePerHour) || 0
    const discount = parseFloat(formData.groupDiscount) || 0.8
    
    // 确保计算结果有效
    if (isNaN(basePrice) || basePrice <= 0) return 0
    if (isNaN(discount) || discount <= 0) return 0
    
    return Math.round(basePrice * discount)
  }

  // 计算每个学生的平均价格
  const calculateAveragePrice = (studentCount: number) => {
    if (studentCount === 0) return 0
    
    // 获取群组总价
    let totalPrice = 0
    if (formData.groupPricePerHour && !isNaN(parseFloat(formData.groupPricePerHour))) {
      totalPrice = parseFloat(formData.groupPricePerHour)
    } else {
      totalPrice = calculateGroupPrice()
    }
    
    // 如果总价仍然无效，返回0
    if (isNaN(totalPrice) || totalPrice <= 0) return 0
    
    return Math.round(totalPrice / studentCount * 100) / 100
  }

  // 生成不同人数的价格预览
  const generatePriceBreakdown = () => {
    const minStudents = parseInt(formData.minStudents) || 2
    const maxStudents = parseInt(formData.maxStudents) || 6
    const breakdown = []
    
    // 确保最少和最多学生数有效
    if (isNaN(minStudents) || isNaN(maxStudents) || minStudents <= 0 || maxStudents <= 0) {
      return []
    }
    
    for (let i = minStudents; i <= Math.min(maxStudents, minStudents + 4); i++) {
      const totalPrice = formData.groupPricePerHour && !isNaN(parseFloat(formData.groupPricePerHour)) 
        ? parseFloat(formData.groupPricePerHour) 
        : calculateGroupPrice()
        
      breakdown.push({
        studentCount: i,
        averagePrice: calculateAveragePrice(i),
        totalPrice: totalPrice
      })
    }
    
    return breakdown
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UsersIcon className="h-8 w-8 mr-3 text-purple-600" />
                创建群组课程
              </h1>
              <p className="mt-2 text-gray-600">创建一对多课程，让多个学生一起学习</p>
            </div>
            <button
              onClick={() => navigate('/teacher/courses')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              取消
            </button>
          </div>
        </div>

        {/* 说明卡片 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">群组课程说明：</p>
              <ul className="list-disc list-inside space-y-1">
                <li>设置最小和最大学生数量，达到最小人数即可开课</li>
                <li>群组价格通常比一对一课程更优惠，吸引更多学生</li>
                <li>可以设置群组折扣，增加课程竞争力</li>
                <li>支持先到先得的预约模式</li>
              </ul>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <BookOpenIcon className="h-5 w-5 mr-2 text-blue-600" />
              基本信息
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 课程标题 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  课程标题 *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例如：高中数学群组辅导"
                />
              </div>

              {/* 科目选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  科目 *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              {/* 课程时长 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  课程时长（小时）*
                </label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="0.5">0.5小时</option>
                  <option value="1">1小时</option>
                  <option value="1.5">1.5小时</option>
                  <option value="2">2小时</option>
                  <option value="2.5">2.5小时</option>
                  <option value="3">3小时</option>
                </select>
              </div>

              {/* 课程描述 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  课程描述 *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="详细描述课程内容、适合对象、学习目标等..."
                />
              </div>
            </div>
          </div>

          {/* 群组设置 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <UsersIcon className="h-5 w-5 mr-2 text-purple-600" />
              群组设置
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 最小学生数 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  最小学生数 *
                </label>
                <input
                  type="number"
                  name="minStudents"
                  value={formData.minStudents}
                  onChange={handleChange}
                  min="1"
                  max="20"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">达到最小人数即可开课</p>
              </div>

              {/* 最大学生数 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  最大学生数 *
                </label>
                <input
                  type="number"
                  name="maxStudents"
                  value={formData.maxStudents}
                  onChange={handleChange}
                  min="2"
                  max="50"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">群组课程至少需要2人</p>
              </div>

              {/* 群组课程特殊说明 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  群组课程特殊说明
                </label>
                <textarea
                  name="groupDescription"
                  value={formData.groupDescription}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例如：适合基础薄弱的学生，小班教学，更多互动..."
                />
              </div>

              {/* 允许单个学生预约 */}
              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="allowSingleStudent"
                    checked={formData.allowSingleStudent}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    允许单个学生创建新的群组时间段
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">勾选后，学生可以创建新的时间段等待其他人加入</p>
              </div>
            </div>
          </div>

          {/* 价格设置 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-600" />
              价格设置
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 期望价格 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  一对一期望价格（元/小时）*
                </label>
                <input
                  type="number"
                  name="expectedPricePerHour"
                  value={formData.expectedPricePerHour}
                  onChange={handleChange}
                  min="1"
                  step="0.01"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">用于计算群组课程价格的基准</p>
              </div>

              {/* 群组折扣 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  群组折扣率
                </label>
                <select
                  name="groupDiscount"
                  value={formData.groupDiscount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="0.5">50% (5折)</option>
                  <option value="0.6">60% (6折)</option>
                  <option value="0.7">70% (7折)</option>
                  <option value="0.8">80% (8折)</option>
                  <option value="0.9">90% (9折)</option>
                  <option value="1">100% (无折扣)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">相对于一对一价格的折扣</p>
              </div>

              {/* 自定义群组价格 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  自定义群组价格（元/小时）
                </label>
                <input
                  type="number"
                  name="groupPricePerHour"
                  value={formData.groupPricePerHour}
                  onChange={handleChange}
                  min="1"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`自动计算: ${calculateGroupPrice()}`}
                />
                <p className="text-xs text-gray-500 mt-1">留空将使用折扣自动计算</p>
              </div>
            </div>

            {/* 价格预览 */}
            {formData.expectedPricePerHour && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">价格预览</h3>
                
                {/* 基础价格信息 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-600">一对一价格：</span>
                    <span className="font-medium text-green-600 ml-2">¥{formData.expectedPricePerHour}/小时</span>
                  </div>
                  <div>
                    <span className="text-gray-600">群组总价：</span>
                    <span className="font-medium text-blue-600 ml-2">¥{formData.groupPricePerHour || calculateGroupPrice()}/小时</span>
                  </div>
                </div>

                {/* 平均价格预览表 */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">💡 每人平均价格预览</h4>
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            学生人数
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            每人支付
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            相比一对一
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {generatePriceBreakdown().map((item, index) => {
                          const oneToOnePrice = parseFloat(formData.expectedPricePerHour) || 0
                          const savings = oneToOnePrice - item.averagePrice
                          const savingsPercent = oneToOnePrice > 0 ? Math.round((savings / oneToOnePrice) * 100) : 0
                          
                          return (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                <span className="font-medium">{item.studentCount}人</span>
                                {item.studentCount === parseInt(formData.minStudents) && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    最少
                                  </span>
                                )}
                                {item.studentCount === parseInt(formData.maxStudents) && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    最多
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                <span className="font-medium text-blue-600">¥{item.averagePrice.toFixed(2)}/小时</span>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {savings > 0 ? (
                                  <span className="text-green-600 font-medium">
                                    省¥{savings.toFixed(2)} ({savingsPercent}%↓)
                                  </span>
                                ) : (
                                  <span className="text-gray-500">无优惠</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💰 提示：人数越多，每个学生分摊的费用越少，课程更有吸引力！
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* 提交按钮 */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/teacher/courses')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  创建中...
                </>
              ) : (
                <>
                  <UsersIcon className="h-4 w-4 mr-2" />
                  创建群组课程
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateGroupCourse 